import { defineMessages } from 'react-intl';

/**
 * The one-off publish copy (fym-shared `music24/oneOffPublish.ts`): the channel badges on the asset
 * cards, the "Publish to YouTube?" prompt and the Studio switch. The native apps render the same
 * ids. `{channel}` is a brand name (YouTube, Facebook, Instagram, Spotify).
 */
export const publishMessages = defineMessages({
  badgeNone: { id: 'app.sml.publish.badge.none', defaultMessage: 'Not on {channel}' },
  badgePublished: { id: 'app.sml.publish.badge.published', defaultMessage: 'On {channel}' },
  badgeScheduled: { id: 'app.sml.publish.badge.scheduled', defaultMessage: 'On {channel} at {time}' },
  badgeWaiting: { id: 'app.sml.publish.badge.waiting', defaultMessage: 'Goes to {channel} when ready' },
  badgeUploading: { id: 'app.sml.publish.badge.uploading', defaultMessage: 'Uploading to {channel}' },
  badgeFailed: { id: 'app.sml.publish.badge.failed', defaultMessage: '{channel} upload failed' },
  badgeRemoved: { id: 'app.sml.publish.badge.removed', defaultMessage: 'Removed from {channel}' },
  badgePrivate: { id: 'app.sml.publish.badge.private', defaultMessage: 'Private on {channel}' },
  badgeCanceled: { id: 'app.sml.publish.badge.canceled', defaultMessage: 'Private on {channel}, no time' },
  canvasSet: { id: 'app.sml.publish.badge.canvasSet', defaultMessage: 'Canvas set in Spotify' },
  canvasNotSet: { id: 'app.sml.publish.badge.canvasNotSet', defaultMessage: 'Canvas not set in Spotify' },
  badgeNotYet: { id: 'app.sml.publish.badge.notYet', defaultMessage: 'Not on {channel} yet' },
  badgeSet: { id: 'app.sml.publish.badge.set', defaultMessage: 'Set on {channel}' },
  badgeNotSet: { id: 'app.sml.publish.badge.notSet', defaultMessage: 'Not set on {channel}' },
  // A slot with several files (the four YouTube thumbnails): one badge with the count.
  badgeSetSome: {
    id: 'app.sml.publish.badge.setSome',
    defaultMessage: 'Set on {channel} · {count} of {total}',
  },
  badgeOnSome: {
    id: 'app.sml.publish.badge.onSome',
    defaultMessage: 'On {channel} · {count} of {total}',
  },
  actionDone: { id: 'app.sml.publish.action.done', defaultMessage: 'Done' },
  filesTitle: { id: 'app.sml.publish.files.title', defaultMessage: 'Which files are on {channel}?' },
  filesLead: {
    id: 'app.sml.publish.files.lead',
    defaultMessage: 'Mark each file that you set on {channel}. Download a file first if you need it.',
  },

  actionPublish: { id: 'app.sml.publish.action.publish', defaultMessage: 'Publish to {channel}' },
  actionOpen: { id: 'app.sml.publish.action.open', defaultMessage: 'Open on {channel}' },
  actionChangeTime: { id: 'app.sml.publish.action.changeTime', defaultMessage: 'Change the time' },
  actionCancel: { id: 'app.sml.publish.action.cancel', defaultMessage: 'Cancel the publish' },
  actionRetry: { id: 'app.sml.publish.action.retry', defaultMessage: 'Try again' },
  actionConnect: { id: 'app.sml.publish.action.connect', defaultMessage: 'Connect {channel}' },
  actionReconnect: { id: 'app.sml.publish.action.reconnect', defaultMessage: 'Reconnect {channel} to change the time' },
  actionMarkCanvas: { id: 'app.sml.publish.action.markCanvas', defaultMessage: 'I set it in Spotify for Artists' },
  actionMarkPosted: { id: 'app.sml.publish.action.markPosted', defaultMessage: 'I posted it on {channel}' },
  actionUnmark: { id: 'app.sml.publish.action.unmark', defaultMessage: 'Remove the mark' },
  actionDownload: { id: 'app.sml.publish.action.download', defaultMessage: 'Download the file' },
  // design: assets-rethink b3 — the manual marks of the Kit tab.
  actionAddedIt: { id: 'app.sml.publish.action.addedIt', defaultMessage: 'I added it' },
  actionSetIt: { id: 'app.sml.publish.action.setIt', defaultMessage: 'I set it' },
  actionUndoMark: { id: 'app.sml.publish.action.undoMark', defaultMessage: 'Undo the mark' },
  howToTitleCanvas: { id: 'app.sml.publish.howTo.canvas.title', defaultMessage: 'Add the Canvas on Spotify' },
  howToLeadCanvas: {
    id: 'app.sml.publish.howTo.canvas.lead',
    defaultMessage: 'Spotify has no API for this. You add it one time.',
  },
  howToCanvas1: {
    id: 'app.sml.publish.howTo.canvas.step1',
    defaultMessage: 'Download the Canvas loop (MP4, 8 s, no sound).',
  },
  howToCanvas2: {
    id: 'app.sml.publish.howTo.canvas.step2',
    defaultMessage: 'Open Spotify for Artists, then Music, then the song.',
  },
  howToCanvas3: { id: 'app.sml.publish.howTo.canvas.step3', defaultMessage: 'Click "Add Canvas" and upload the file.' },
  howToCanvas4: {
    id: 'app.sml.publish.howTo.canvas.step4',
    defaultMessage: 'Come back here and click "I added it".',
  },
  howToTitleApple: {
    id: 'app.sml.publish.howTo.apple.title',
    defaultMessage: 'Add the motion artwork on Apple Music',
  },
  howToLeadApple: {
    id: 'app.sml.publish.howTo.apple.lead',
    defaultMessage: 'Apple Music takes motion artwork only from your distributor. You add it one time.',
  },
  howToApple1: {
    id: 'app.sml.publish.howTo.apple.step1',
    defaultMessage: 'Download the motion artwork (square and tall).',
  },
  howToApple2: {
    id: 'app.sml.publish.howTo.apple.step2',
    defaultMessage: 'Send both files to your distributor with the release, or add them where it asks for motion art.',
  },
  howToApple3: {
    id: 'app.sml.publish.howTo.apple.step3',
    defaultMessage: 'Come back here and click "I added it".',
  },
  howToTitleSet: { id: 'app.sml.publish.howTo.set.title', defaultMessage: 'Set it on {channel}' },
  howToLeadSet: {
    id: 'app.sml.publish.howTo.set.lead',
    defaultMessage: '{channel} has no way for us to set it. You set it one time.',
  },
  howToSet1: { id: 'app.sml.publish.howTo.set.step1', defaultMessage: 'Download the file.' },
  howToSet2: {
    id: 'app.sml.publish.howTo.set.step2',
    defaultMessage: 'Open your profile on {channel} and upload it.',
  },
  howToSet3: { id: 'app.sml.publish.howTo.set.step3', defaultMessage: 'Come back here and click "I set it".' },
  howToTitleSpotifyHeader: {
    id: 'app.sml.publish.howTo.spotifyHeader.title',
    defaultMessage: 'Put your header on Spotify',
  },
  howToLeadSpotifyHeader: {
    id: 'app.sml.publish.howTo.spotifyHeader.lead',
    defaultMessage: 'Spotify has no way for us to set it. It takes you a minute.',
  },
  howToSpotifyHeader1: {
    id: 'app.sml.publish.howTo.spotifyHeader.step1',
    defaultMessage: 'Download the file, then open Spotify for Artists and pick your artist.',
  },
  howToSpotifyHeader2: {
    id: 'app.sml.publish.howTo.spotifyHeader.step2',
    defaultMessage: 'Go to Profile, then Header image, then Upload, and pick the file.',
  },
  howToSpotifyHeader3: {
    id: 'app.sml.publish.howTo.spotifyHeader.step3',
    defaultMessage: 'Check the desktop and the phone preview, save, and come back to click "I set it".',
  },
  howToNoReminder: {
    id: 'app.sml.publish.howTo.noReminder',
    defaultMessage: 'We send no reminder. "Undo the mark" takes it back.',
  },

  promptTitle: { id: 'app.sml.publish.prompt.title', defaultMessage: 'Publish to {channel}?' },
  promptReady: { id: 'app.sml.publish.prompt.ready', defaultMessage: '{asset} is ready.' },
  promptGoesTo: { id: 'app.sml.publish.prompt.goesTo', defaultMessage: 'Goes to' },
  promptVideoTitle: { id: 'app.sml.publish.prompt.videoTitle', defaultMessage: 'Title' },
  promptDescription: { id: 'app.sml.publish.prompt.description', defaultMessage: 'Description' },
  promptWhen: { id: 'app.sml.publish.prompt.when', defaultMessage: 'When does it go public?' },
  timingNow: { id: 'app.sml.publish.timing.now', defaultMessage: 'Now' },
  timingReleaseDay: { id: 'app.sml.publish.timing.releaseDay', defaultMessage: 'On release day ({date})' },
  timingLater: { id: 'app.sml.publish.timing.later', defaultMessage: 'Later' },
  timingPick: { id: 'app.sml.publish.timing.pick', defaultMessage: 'Date and time' },
  privateUntil: {
    id: 'app.sml.publish.prompt.privateUntil',
    defaultMessage: 'We upload it now as private. {channel} makes it public at {time}.',
  },
  publicNow: { id: 'app.sml.publish.prompt.publicNow', defaultMessage: 'We upload it now. It is public at once.' },
  submit: { id: 'app.sml.publish.prompt.submit', defaultMessage: 'Publish' },
  submitSave: { id: 'app.sml.publish.prompt.save', defaultMessage: 'Save the time' },
  notNow: { id: 'app.sml.publish.prompt.notNow', defaultMessage: 'Not now' },
  smartLinkNote: {
    id: 'app.sml.publish.prompt.smartLinkNote',
    defaultMessage: 'When the video is public, your SmartLink plays it from YouTube, so the views count there.',
  },
  canvasHowTo: {
    id: 'app.sml.publish.canvas.howTo',
    defaultMessage:
      'Spotify has no Canvas API. Download the loop, add it to the song in Spotify for Artists, then mark it here.',
  },
  instagramHowTo: {
    id: 'app.sml.publish.instagram.howTo',
    defaultMessage:
      'Instagram takes a long video only from its own app. Download the video, post it, then mark it here.',
  },

  studioSwitch: { id: 'app.sml.publish.studio.switch', defaultMessage: 'Publish to YouTube when ready' },
  studioSwitchHint: {
    id: 'app.sml.publish.studio.switchHint',
    defaultMessage: 'We upload the video to {name} when it is ready.',
  },
  studioConnect: { id: 'app.sml.publish.studio.connect', defaultMessage: 'Connect YouTube to publish it there' },
  studioOn: { id: 'app.sml.publish.studio.on', defaultMessage: 'It goes to YouTube when it is ready.' },

  failRenderFailed: {
    id: 'app.sml.publish.fail.renderFailed',
    defaultMessage: 'The video failed, so nothing went up.',
  },
  failReconnect: {
    id: 'app.sml.publish.fail.reconnect',
    defaultMessage: 'The login stopped working. Reconnect the channel.',
  },
  failFileGone: { id: 'app.sml.publish.fail.fileGone', defaultMessage: 'The file is gone. Make the video again.' },
  failPlatformRefused: {
    id: 'app.sml.publish.fail.platformRefused',
    defaultMessage: 'The platform refused the video.',
  },
  failUploadFailed: {
    id: 'app.sml.publish.fail.uploadFailed',
    defaultMessage: 'The upload did not finish. Try again.',
  },

  errorTimeInvalid: {
    id: 'app.sml.publish.error.timeInvalid',
    defaultMessage: 'Pick a time at least 15 minutes from now.',
  },
  errorAlready: { id: 'app.sml.publish.error.already', defaultMessage: 'This video is already on {channel}.' },
  errorNotConnected: { id: 'app.sml.publish.error.notConnected', defaultMessage: 'Connect {channel} first.' },
  errorGeneric: { id: 'app.sml.publish.error.generic', defaultMessage: 'That did not work. Try again.' },

  badgeReplaced: { id: 'app.sml.publish.badge.replaced', defaultMessage: 'Replaced on {channel}' },
  submitReel: { id: 'app.sml.publish.prompt.submitReel', defaultMessage: 'Post now as a Reel' },
  promptCaption: { id: 'app.sml.publish.prompt.caption', defaultMessage: 'Caption' },
  reelNow: {
    id: 'app.sml.publish.prompt.reelNow',
    defaultMessage: 'Instagram posts it now as a Reel. Instagram cannot wait for a time.',
  },
  replaceTitle: {
    id: 'app.sml.publish.replace.title',
    defaultMessage: 'Replace the video on YouTube?',
  },
  replaceLive: {
    id: 'app.sml.publish.replace.live',
    defaultMessage: 'Revision r{revision} of this video is on YouTube.',
  },
  replaceYes: {
    id: 'app.sml.publish.replace.yes',
    defaultMessage: 'Replace it: the old video turns private when the new one is public',
  },
  replaceKeepBoth: { id: 'app.sml.publish.replace.keepBoth', defaultMessage: 'Keep both videos on YouTube' },
  refusalSize: {
    id: 'app.sml.publish.refusal.size',
    defaultMessage: 'This video is {size}. Instagram takes a Reel of at most {max}.',
  },
  refusalTooLong: {
    id: 'app.sml.publish.refusal.tooLong',
    defaultMessage: 'This video runs {length}. Instagram takes a Reel of at most {max}.',
  },
  refusalTooShort: {
    id: 'app.sml.publish.refusal.tooShort',
    defaultMessage: 'This video runs {length}. Instagram takes a Reel of at least {min}.',
  },
  refusalOther: {
    id: 'app.sml.publish.refusal.other',
    defaultMessage: 'Instagram does not take this video as a Reel.',
  },
  refusalFallback: {
    id: 'app.sml.publish.refusal.fallback',
    defaultMessage: 'Post it from the Instagram app, then mark it here.',
  },
});
